import { action, computed, observable } from 'mobx';
import { isValidEmail } from 'app/utils/validators';
import TicketsBaseFormStore from 'modules/ticketPublicPage/stores/common/form';
import view from 'modules/ticketPublicPage/stores/vouchers/view';
import facility from 'app/facility';

export class VouchersFormStore extends TicketsBaseFormStore {
  constructor(view) {
    super();
    this.view = view;
    this.setFormDefaults();
  }
  WITHDRAWAL_INITIAL_STEP = 'withdrawal_initial';
  WITHDRAWAL_FINAL_STEP = 'withdrawal_final';
  WITHDRAWAL_SUMMARY_STEP = 'withdrawal_summary';
  WITHDRAWAL_SUCCESS_STEP = 'withdrawal_success';
  CONTACT_DETAILS = 'Contact Details';
  PAYMENT_DETAILS = 'Payment Details';

  @observable accessor isOwnValue = false;
  @observable accessor isWithdrawal = false;

  @computed get showBackButton() {
    return ![
      this.INITIAL,
      this.WITHDRAWAL_INITIAL_STEP,
      this.CONFIRMATION,
      this.WITHDRAWAL_SUCCESS_STEP,
    ].includes(this.step);
  }

  @computed get hideBuyButton() {
    return (
      (this.formValues.get('payment_type') === this.paymentTypes.paypal &&
        this.step === this.PAYMENT_DETAILS) ||
      this.step === this.CONFIRMATION
    );
  }

  @computed get errorTexts() {
    let errorFields;
    switch (this.step) {
      case this.INITIAL:
        errorFields = ['price', 'sender', 'recipient', 'greeting_message'];
        break;
      case this.CONTACT_DETAILS:
        errorFields = [
          'email',
          'recipient_name',
          'recipient_surname',
          'name',
          'surname',
        ];
        break;
      default:
        errorFields = [];
    }
    return errorFields
      .map((field) => this.errors.get(field))
      .filter((err) => !!err)
      .reduce((r, c) => r.add(c), new Set());
  }

  @action setOwnValueClickedStatus = (isOwnValue) => {
    this.isOwnValue = isOwnValue;
  };

  @action onNextBtnClick = async () => {
    this.validate();
    if (this.hasErrors) return;
    switch (this.step) {
      case this.INITIAL:
        this.step = this.CONTACT_DETAILS;
        break;
      case this.CONTACT_DETAILS:
        this.step = this.PAYMENT_DETAILS;
        break;
      case this.PAYMENT_DETAILS:
        if (this.formValues.get('payment_type') === this.paymentTypes.bambora) {
          const price = Number(this.formValues.get('price'));
          await this.handleBamboraCheckout(price);
        }
        this.validatePayment();
        break;
      case this.CONFIRMATION:
        this.step = this.INITIAL;
        this.setFormDefaults();
        break;
      case this.WITHDRAWAL_INITIAL_STEP:
        if (await this.confirmVoucherValidity()) {
          this.step = this.WITHDRAWAL_SUMMARY_STEP;
        }
        break;
      case this.WITHDRAWAL_SUMMARY_STEP:
        this.step = this.WITHDRAWAL_FINAL_STEP;
        break;
      case this.WITHDRAWAL_FINAL_STEP:
        if (await this.submitWithdrawalCancellation()) {
          this.step = this.WITHDRAWAL_SUCCESS_STEP;
        }
        break;
      case this.WITHDRAWAL_SUCCESS_STEP:
        this.step = this.INITIAL;
        this.setFormDefaults();
        this.isWithdrawal = false;
        break;
    }
  };

  @action onBackBtnClick = () => {
    switch (this.step) {
      case this.CONTACT_DETAILS:
        this.step = this.INITIAL;
        break;
      case this.PAYMENT_DETAILS:
        this.step = this.CONTACT_DETAILS;
        break;
      case this.WITHDRAWAL_SUMMARY_STEP:
        this.step = this.WITHDRAWAL_INITIAL_STEP;
        break;
      case this.WITHDRAWAL_FINAL_STEP:
        this.step = this.WITHDRAWAL_SUMMARY_STEP;
        break;
    }
    this.errors.clear();
  };

  @action submitWithdrawalCancellation = async () => {
    const purchase = this.formValues.get('withdrawal_voucher_purchase') || {};
    const purchaseId = purchase.id;
    const withdrawalReason = this.formValues.get('withdrawal_reason');

    if (!purchaseId) {
      this.serverError = i18n.gettext('Unable to submit cancellation request.');
      return false;
    }

    this.serverError = null;

    try {
      await this.view.purchaseDataStore.requestWithdrawalCancellation(
        purchaseId,
        withdrawalReason,
        {
          refNum: purchase.ref_num,
          email: purchase.email,
          buyerName: purchase.buyer_name,
        }
      );
      return true;
    } catch (error) {
      this.serverError = this.parseError(error);
      return false;
    }
  };

  @action setCustomFormDefaults() {
    this.formValues.set('price', this.view.defaultPrice);
    this.formValues.set('sender', '');
    this.formValues.set('recipient', '');
    this.formValues.set('recipient_surname', '');
    this.formValues.set('recipient_name', '');
    this.formValues.set('name', '');
    this.formValues.set('surname', '');
    this.formValues.set('email', '');
    this.formValues.set('owner_name', '');
    this.formValues.set('owner_email', '');
    this.formValues.set('download_url', '');
    this.formValues.set('newsletter_subscribe', false);
    this.formValues.set('withdrawal_code', '');
    this.formValues.set('withdrawal_email', '');
    this.formValues.set('withdrawal_name', '');
    this.formValues.set('withdrawal_reason', '');
    this.formValues.set('withdrawal_voucher_purchase', null);
    this.isOwnValue = false;
  }

  @action confirmVoucherValidity = async () => {
    const code = (this.formValues.get('withdrawal_code') || '').trim();
    const email = (this.formValues.get('withdrawal_email') || '').trim();
    const buyerName = (this.formValues.get('withdrawal_name') || '').trim();

    this.validateField('withdrawal_code');
    this.validateField('withdrawal_email');
    this.validateField('withdrawal_name');

    if (
      this.errors.has('withdrawal_code') ||
      this.errors.has('withdrawal_email') ||
      this.errors.has('withdrawal_name')
    ) {
      return false;
    }

    this.serverError = null;
    this.formValues.set('withdrawal_voucher_purchase', null);

    try {
      const matchingVoucher =
        await this.view.purchaseDataStore.verifyWithdrawalIdentity(
          code,
          email,
          buyerName
        );

      this.formValues.set('withdrawal_voucher_purchase', matchingVoucher);
      return true;
    } catch (error) {
      this.serverError = this.parseError(error);
      return false;
    }
  };

  @action validate = () => {
    let validateFields = [];
    switch (this.step) {
      case this.INITIAL:
        validateFields = ['price', 'sender', 'recipient', 'greeting_message'];
        break;
      case this.CONTACT_DETAILS:
        validateFields = [
          'recipient_name',
          'recipient_surname',
          'name',
          'surname',
          'email',
        ];
        break;
      case this.WITHDRAWAL_INITIAL_STEP:
        validateFields = [
          'withdrawal_code',
          'withdrawal_email',
          'withdrawal_name',
        ];
        break;
      case this.WITHDRAWAL_SUMMARY_STEP:
        validateFields = ['withdrawal_reason'];
        break;
      case this.PAYMENT_DETAILS:
        break;
    }
    validateFields.forEach((field) => this.validateField(field));
  };

  @action validateField = (field) => {
    const value = this.formValues.get(field);
    const {
      min_value: minValue,
      max_value: maxValue,
      individual_value_allowed: individualValueAllowed,
    } = this.view.activeObject;
    const { currencyShort } = facility.settings;
    switch (field) {
      case 'price':
        if (
          individualValueAllowed &&
          (this.isOwnValue || view.hasNoFixedValues)
        ) {
          if (value < minValue || value > maxValue)
            this.setError(
              field,
              i18n.sprintf(
                i18n.gettext('Please set a price between %s %s and %s %s.'),
                minValue,
                currencyShort,
                maxValue,
                currencyShort
              )
            );
        }
        if (parseFloat(value.toString().replace(',', '.')) % 1 !== 0) {
          this.setError(
            field,
            i18n.gettext('Please enter a smooth value. Thank you!')
          );
        }
        if (!value) {
          this.setError(field, i18n.gettext('Please input a price.'));
        }
        break;
      case 'email':
        if (!isValidEmail(value))
          this.setError(
            field,
            i18n.gettext('Please input a valid email address.')
          );
        break;
      case 'withdrawal_email':
        if (!value) {
          this.setError(
            field,
            i18n.gettext('Please input your email address.')
          );
        } else if (!isValidEmail(value)) {
          this.setError(
            field,
            i18n.gettext('Please input the valid email address.')
          );
        }
        break;
      case 'withdrawal_code':
        if (!value) {
          this.setError(field, i18n.gettext('Please input the voucher code.'));
        }
        break;
      case 'withdrawal_name':
        if (!value) {
          this.setError(
            field,
            i18n.gettext('Please input the buyers full name.')
          );
        }
        break;
      case 'withdrawal_reason':
        if (value && value.length > 256) {
          this.setError(
            field,
            i18n.sprintf(
              i18n.gettext('The reason cannot exceed %d characters'),
              256
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'recipient':
        if (!value) {
          this.setError(field, i18n.gettext('Please add a recipient.'));
        }
        break;
      case 'sender':
        if (!value) {
          this.setError(field, i18n.gettext('Please add a sender.'));
        }
        break;
      case 'recipient_name':
        if (!value) {
          this.setError(
            field,
            i18n.gettext('Please add the recipients first name.')
          );
        }
        break;
      case 'recipient_surname':
        if (!value) {
          this.setError(
            field,
            i18n.gettext('Please add the recipients last name.')
          );
        }
        break;
      case 'name':
        if (!value) {
          this.setError(field, i18n.gettext('Please add your first name.'));
        }
        break;
      case 'surname':
        if (!value) {
          this.setError(field, i18n.gettext('Please add your last name.'));
        }
        break;
      default:
        if (!this.formValues.get(field) && field !== 'greeting_message')
          this.setError(field, i18n.gettext('Please fill in the field.'));
        break;
    }
  };

  convertFormToServer = () => {
    const converted = { ticket_id: this.view.activeObject.id };
    Array.from(this.formValues.keys()).map((key) => {
      switch (key) {
        case 'price':
          converted[key] = this.formValues
            .get(key)
            .toString()
            .replace(',', '.');
          break;
        case 'withdrawal_reason':
          converted['reason'] = this.formValues.get('withdrawal_reason');
          break;
        default:
          converted[key] = this.formValues.get(key);
          break;
      }
    });
    return converted;
  };
}

const form = new VouchersFormStore(view);
export default form;
